<script setup lang="ts">
import type { Ref } from "vue";
import { ref } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import { Filters } from "~/core/models/filters";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import type { User } from "~/modules/user/models/User.interface";
import type { UserSearch } from "~/modules/user/models/UserSearch.interface";

const { t } = useI18n();

const props = defineProps<{
  declinations?: string[];
  name?: string;
  modelValue?: User[];
  noAction?: boolean;
  selectedUsers?: User[];
  types?: string[];
  businessUnitCode?: number;
  organizationCode?: number;
}>();
const emit = defineEmits(["update:modelValue"]);

const {
  isLoading,
  results: users,
  fetchData,
  setFilter,
  search,
  onSearch,
} = useFetchUsers({
  searchMode: SearchMode.FILTER_SEARCH,
  organizationCode: props.organizationCode,
  businessUnitCode: props.businessUnitCode,
});

const filterMap: Ref<Map<Filters, any> | undefined> = ref();

function userDataToUserSearch(user: User): UserSearch {
  const { login, email, firstName, lastName, active, profile } = user;

  return {
    email,
    firstName,
    fullName: login,
    lastName,
    login,
    active,
    profile,
  };
}

async function loadData(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();
  await fetchData(page, pageSize, params);
}

// Apply provided default filters
const updateListFilter = () => {
  if (filterMap.value) {
    for (const [filter, value] of filterMap.value) {
      setFilter(filter, value ?? "");
    }
  }
};

watch(
  () => props.name,
  (filterByName) => {
    if (!filterMap.value) {
      filterMap.value = new Map<Filters, any>();
    }
    if (!filterByName) {
      if (filterMap.value.has(Filters.USER_FIRST_OR_LAST_NAME)) {
        filterMap.value.set(Filters.USER_FIRST_OR_LAST_NAME, "");
      }
    } else {
      filterMap.value.set(Filters.USER_FIRST_OR_LAST_NAME, filterByName);
    }

    updateListFilter();
    search();
  },
);

const visibleData = computed(() => {
  return (users.value?.results.length ?? 0 <= 10)
    ? users.value?.results.map(userDataToUserSearch)
    : (users.value?.results.slice(0, 10).map(userDataToUserSearch) ?? []);
});

const selectedItems = computed({
  get() {
    if (props.modelValue) {
      return props.modelValue.map((user) => user.login ?? "");
    }

    return [];
  },
  set(selected?: string[]) {
    if (selected != null) {
      const userItems: User[] = selected
        .map((currentUserId) => {
          // If the device is not in the user list found, use modelValue where there was the previous selected user list
          let user = users.value?.results.find((currentUser) => {
            return currentUser.login === currentUserId;
          });

          if (!user) {
            user = props.modelValue?.find(
              (user) => user.login === currentUserId,
            );
          }
          return user;
        })
        .filter((currentUser) => currentUser !== undefined) as User[];
      emit("update:modelValue", userItems);
    } else {
      emit("update:modelValue", []);
    }
  },
});

onSearch(loadData);
</script>

<template>
  <div class="pb-6 px-6">
    <filter-selectable-list
      v-model="selectedItems"
      :data="visibleData"
      :is-loading="isLoading"
      :empty-text="t('user.empty')"
      :header="t('logs.filters.entityClass.value.user')"
      header-class="underline text-base"
      prop="fullName"
      item-prop="login"
    />
  </div>
</template>
